import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trackPageView, trackClick, trackNavigation } from '../utils/analytics';
import UserHeader from '../components/UserHeader';
import BottomNav from '../components/BottomNav';
import cloudsImg from '../assets/webp/clouds.webp';
import childTorahScrollImg from '../assets/webp/child-torah-scroll.webp';
import parentsCoupleImg from '../assets/webp/parents-couple.webp';
import { getConnectionsForUser, Connection } from '../utils/parentStudentFirestore';
import { unlinkConnection } from '../utils/connectionCodes';

interface LoggedInUser {
  email?: string;
  displayName?: string;
  name?: string;
}

function readLoggedInUser(): LoggedInUser | null {
  try {
    const raw = localStorage.getItem('loggedInUser');
    return raw ? (JSON.parse(raw) as LoggedInUser) : null;
  } catch {
    return null;
  }
}

const PlusCircleIcon: React.FC = () => (
  <svg
    className="w-[22px] h-[22px] flex-shrink-0"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="#1A1A2E" strokeWidth="2" />
    <line x1="12" y1="8" x2="12" y2="16" stroke="#1A1A2E" strokeWidth="2" strokeLinecap="round" />
    <line x1="8" y1="12" x2="16" y2="12" stroke="#1A1A2E" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

interface MenuItemDef {
  label: string;
  danger?: boolean;
  onClick: () => void;
}

interface ConnectionRowProps {
  name: string;
  roleLabel: string;
  avatarLetter: string;
  avatarBg: string;
  avatarText: string;
  menuOpen: boolean;
  onMenuToggle: () => void;
  menuItems: MenuItemDef[];
  highlighted?: boolean;
}

const ConnectionRow: React.FC<ConnectionRowProps> = ({
  name,
  roleLabel,
  avatarLetter,
  avatarBg,
  avatarText,
  menuOpen,
  onMenuToggle,
  menuItems,
  highlighted,
}) => (
  <div
    className={`flex items-center h-14 px-4 gap-3 rounded-2xl shadow ${highlighted ? 'bg-[#fffbe9]' : 'bg-[#EDF5FE]'}`}
  >
    {/* Avatar */}
    <div
      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${avatarBg} ${avatarText}`}
    >
      {avatarLetter}
    </div>

    {/* Name */}
    <div className="text-base font-bold text-[#424656] truncate max-w-[45%] flex-shrink-0">
      {name}
    </div>

    <div className="flex-1" />

    {/* Role label */}
    <div className="text-sm font-medium text-[#4C9A52] truncate max-w-[35%] flex-shrink-0">
      {roleLabel}
    </div>

    {/* Three-dot menu */}
    <div className="relative flex-shrink-0">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onMenuToggle();
        }}
        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 text-lg leading-none"
        aria-label="options"
      >
        ⋮
      </button>
      {menuOpen && (
        <div
          className="absolute left-0 top-8 z-50 bg-white rounded-xl shadow-lg border border-gray-100 w-32 text-right"
          onClick={(e) => e.stopPropagation()}
        >
          {menuItems.map((item, i) => (
            <React.Fragment key={item.label}>
              {i > 0 && <div className="border-t border-gray-100" />}
              <button
                type="button"
                className={`block w-full px-4 py-2 text-sm text-right hover:bg-gray-50 ${item.danger ? 'text-red-500' : 'text-[#424656]'}`}
                onClick={item.onClick}
              >
                {item.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  </div>
);

const ChooseParentChildPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
  const [unlinkingEmail, setUnlinkingEmail] = useState<string | null>(null);
  const [myAccountType, setMyAccountType] = useState<'student' | 'parent'>(() => {
    try {
      return (localStorage.getItem('userAccountType') as 'student' | 'parent') || 'student';
    } catch {
      return 'student';
    }
  });

  const toggleAccountType = () => {
    const next = myAccountType === 'student' ? 'parent' : 'student';
    setMyAccountType(next);
    try {
      localStorage.setItem('userAccountType', next);
    } catch {
      /* ignore */
    }
  };

  const user = readLoggedInUser();
  const userEmail = (user?.email || '').trim().toLowerCase();

  useEffect(() => {
    trackPageView('ChooseParentChild');
    if (!userEmail) {
      setLoading(false);
      return;
    }
    getConnectionsForUser(userEmail)
      .then(setConnections)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userEmail]);

  const handleConnectParent = () => {
    trackClick('Connect Parent', 'ChooseParentChild');
    trackNavigation('ChooseParentChild', 'ConnectParent', 'button');
    navigate('/connect-parent');
  };

  const handleConnectChild = () => {
    trackClick('Connect Child', 'ChooseParentChild');
    trackNavigation('ChooseParentChild', 'ConnectChild', 'button');
    navigate('/connect-child');
  };

  const handleDisconnect = async (conn: Connection) => {
    if (!userEmail) return;
    setUnlinkingEmail(conn.email);
    try {
      const params =
        conn.role === 'parent'
          ? { parentEmail: conn.email, childEmail: userEmail }
          : { parentEmail: userEmail, childEmail: conn.email };
      await unlinkConnection(params);
      setConnections((prev) => prev.filter((c) => c.email !== conn.email));
    } catch (err) {
      console.error('disconnect error:', err);
    }
    setUnlinkingEmail(null);
    setOpenMenuFor(null);
  };

  return (
    <div
      className="flex flex-col flex-1 min-h-0 bg-white"
      style={{
        backgroundImage: `url(${cloudsImg})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% auto',
        backgroundPosition: 'top center',
        direction: 'rtl',
        fontFamily: "var(--kol-font-sans, 'Heebo', sans-serif)",
      }}
      onClick={() => setOpenMenuFor(null)}
    >
      <UserHeader />

      <div className="flex-1 overflow-y-auto px-[18px] pb-4">
        {/* Page header */}
        <div className="text-center mb-5 pt-2">
          <h1 className="text-2xl font-extrabold text-[#424656] mb-1">
            {t('menu_family_card_title', 'המשפחה שלי')}
          </h1>
          <p className="text-sm text-[#6b7280] leading-relaxed">
            {t('menu_family_card_subtitle', 'חבר הורה או ילד כדי לעקוב אחר ההתקדמות')}
          </p>
        </div>

        {/* Connected family section */}
        <div className="mb-0">
          <div className="text-sm font-semibold text-[#424656] mb-2 text-right">
            {t('family_connected_section', 'משפחה מחוברת')}
          </div>

          <div className="flex flex-col gap-3">
            {/* Me row */}
            <ConnectionRow
              highlighted
              name={t('family_me', 'אני')}
              roleLabel={
                myAccountType === 'student'
                  ? t('family_student_account', 'חשבון תלמיד')
                  : t('family_parent_account', 'חשבון הורה')
              }
              avatarLetter=""
              avatarBg="bg-white border border-gray-300"
              avatarText="text-gray-500"
              menuOpen={openMenuFor === 'me'}
              onMenuToggle={() => setOpenMenuFor(openMenuFor === 'me' ? null : 'me')}
              menuItems={[
                {
                  label:
                    myAccountType === 'student'
                      ? t('family_switch_to_parent', 'עבור להורה')
                      : t('family_switch_to_student', 'עבור לתלמיד'),
                  onClick: () => {
                    toggleAccountType();
                    setOpenMenuFor(null);
                  },
                },
              ]}
            />

            {/* Connected parents / children */}
            {!loading &&
              connections.map((conn) => (
                <ConnectionRow
                  key={conn.email}
                  name={conn.name}
                  roleLabel={
                    conn.role === 'parent'
                      ? t('family_role_parent', 'הורה')
                      : t('family_role_child', 'תלמיד')
                  }
                  avatarLetter={conn.name ? conn.name[0] : '?'}
                  avatarBg={conn.role === 'parent' ? 'bg-blue-100' : 'bg-yellow-100'}
                  avatarText={conn.role === 'parent' ? 'text-blue-600' : 'text-yellow-700'}
                  menuOpen={openMenuFor === conn.email}
                  onMenuToggle={() =>
                    setOpenMenuFor(openMenuFor === conn.email ? null : conn.email)
                  }
                  menuItems={[
                    {
                      label:
                        unlinkingEmail === conn.email
                          ? t('connect_disconnecting', 'מנתק...')
                          : t('connect_disconnect', 'נתק'),
                      danger: true,
                      onClick: () => handleDisconnect(conn),
                    },
                    {
                      label: t('connect_cancel', 'ביטול'),
                      onClick: () => setOpenMenuFor(null),
                    },
                  ]}
                />
              ))}
          </div>
        </div>

        {/* Connect action cards — right below the connections list */}
        <div className="flex flex-col gap-3 pt-4 pb-3">
          {/* Connect parent */}
          <div
            onClick={handleConnectParent}
            className="flex items-center bg-white rounded-2xl shadow-lg pr-4 gap-3 cursor-pointer overflow-visible h-14"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleConnectParent();
              }}
              className="w-10 h-10 rounded-xl bg-[#fecb00] flex items-center justify-center flex-shrink-0 shadow-sm"
              aria-label={t('choose_role_connect_parent_btn', 'חבר הורה') as string}
            >
              <PlusCircleIcon />
            </button>
            <span className="flex-1 text-base font-bold text-[#424656] text-right">
              {t('choose_role_connect_parent', 'אני רוצה לחבר הורה')}
            </span>
            <img
              src={childTorahScrollImg}
              alt=""
              className="h-[100px] mb-[40px] w-[110px] object-contain object-bottom flex-shrink-0"
            />
          </div>

          {/* Connect child */}
          <div
            onClick={handleConnectChild}
            className="overflow-visible flex items-center bg-white rounded-2xl shadow-lg pr-4 gap-3 cursor-pointer h-14"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleConnectChild();
              }}
              className="w-10 h-10 rounded-xl bg-[#fecb00] flex items-center justify-center flex-shrink-0 shadow-sm"
              aria-label={t('choose_role_connect_child_btn', 'חבר ילד') as string}
            >
              <PlusCircleIcon />
            </button>
            <span className="flex-1 text-base font-bold text-[#424656] text-right">
              {t('choose_role_connect_child', 'אני רוצה לחבר ילד')}
            </span>
            <img
              src={parentsCoupleImg}
              alt=""
              className="h-[80px] mb-[23px] w-[110px] object-contain object-bottom flex-shrink-0"
            />
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default ChooseParentChildPage;
